<template>
  <div class="d-flex flex-column">
    <b-row class="mx-4 mt-2 d-flex align-items-center">
      <h4>
        <strong>Review Creatives</strong>
      </h4> 
      
      <b-alert
        v-if="!medicareAutoApproval"
        show 
        variant="info"
        class="p-2 mt-3 ml-4"
      >
        Don’t want to review creatives manually?
        <a
          v-b-tooltip.hover="`Once enabled, new ${ medicareCategory.name } creatives are auto-approved after initial review by Ringba X administrators`"
          href="#" 
          @click.prevent="$bvModal.show('confirm-save-creative-settings')"                
        >
          Enable 
        </a>
        auto-approval        
      </b-alert>
      <b-alert
        v-else
        show 
        variant="info"
        class="p-2 mt-3 ml-4"
      >
        Creatives auto-approval enabled.
        <a
          v-b-tooltip.hover="`Once disabled, you will have to review ${ medicareCategory.name } creatives manually`"
          href="#" 
          @click.prevent="$bvModal.show('confirm-save-creative-settings')"          
        >
          Disable 
        </a>
        auto-approval       
      </b-alert>
      
      <b-col
        cols="12"
        lg="4"
        class="ml-auto text-right pr-0"
      >
        <b-form-input
          v-model="filterByCreativeId"
          type="search"
          placeholder="Search by Creative Id or SMID"
          class="custom-form-input--sm"
          debounce="500"
        />
      </b-col>
    </b-row>

    <div class="panel-holder mt-2">
      <div v-if="suggestedCreativesForReviewByBuyer.length">
        <h5 class="mx-4 mt-3">
          <font-awesome-icon
            :icon="['fas', 'star']"
            class="mr-1"
          />
          Suggested Creatives for Review
        </h5>
        <Panel
          loader-text="Loading Creatives"
          :loading="loadingCreatives"
          hide-header
          :icon="['fas', 'star']"
          class="mt-3"
        >
          <template v-slot:content>
            <ListCreativeReviews
              list-name-suffix="suggested"
              :creative-reviews="suggestedCreativesForReviewCopy"
              simple-view
              :filter-by-creative-id="filterByCreativeId"
            />
          </template>
        </Panel>
      </div>
      
      <Panel
        size="small"
        loader-text="Loading Creatives"
        :loading="loadingCreatives"
        hide-header
        class="mt-3"
      >
        <template v-slot:content>
          <ListCreativeReviews
            list-name-suffix="all"
            :creative-reviews="allCreativeReviewsFiltered"
            :filter-by-creative-id="filterByCreativeId"
            :status-filter="statusFilter"
          />
        </template>
      </Panel>
    </div>
    
    <b-modal
      id="confirm-save-creative-settings"
      ref="confirm-save-creative-settings"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Save Creative Settings"
      button-size="sm"
    >          
      <p class="mb-2">
        <strong><span style="font-size: .9rem;">Are you sure you want to {{ medicareAutoApproval ? 'disable' : 'enable' }} {{ medicareCategory.name }} creatives auto-approval?</span></strong>
      </p>          
      <BaseFormCheckbox
        v-if="!medicareAutoApproval"
        id="medicareApproveExisting"
        v-model="medicareApproveExisting"
        name="medicareApproveExisting"
        checked-value="true"
        unchecked-value="false"
      >
        <small>Tick this checkbox if you also want to approve all pending review {{ medicareCategory.name }} creatives.</small>
      </BaseFormCheckbox>

      <BaseFormCheckbox
        v-if="!medicareAutoApproval"
        id="noLiabilityAccepted"
        v-model="noLiabilityAccepted"
        name="noLiabilityAccepted"
        required    
        require-checked            
      >
        <i><small>I hereby acknowledge and agree that Ringba X assumes no responsibility or liability for reviewing or approving {{ medicareCategory.name }} creatives on my behalf.</small></i>
      </BaseFormCheckbox>

      <div
        v-if="!medicareAutoApproval && !noLiabilityAccepted"
        class="invalid-feedback d-flex mb-2 mt-2 justify-content-center"
      >
        <font-awesome-icon
          :icon="['fas', 'exclamation-triangle']"
          class="mr-1"
          style="margin-top: 2px;"
        />
        Please acknowledge Ringba X assumes no responsibility or liability for reviewing or approving Medicare creatives on your behalf.
      </div>

      <template #modal-footer>        
        <ButtonLoader
          type="button"
          variant="danger"
          size="sm"
          :loading="isLoadingSaveSettings"
          @click="updateCreativeSettings"
        >
          {{ medicareAutoApproval ? 'Disable' : 'Enable' }}
        </ButtonLoader>
        
        <b-button
          type="button"
          variant="secondary"
          size="sm"          
          @click="$bvModal.hide('confirm-save-creative-settings')"
        >
          Cancel
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
        
<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'

import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import formInputMixin from '@/mixins/formInputMixin'
import pageTrackingMixin from '@/mixins/ms-clarity-mixin'
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import ListCreativeReviews from './ListCreativeReviews.vue'
        
export default {
  name: 'ReviewCreatives',

  components: {
    ListCreativeReviews,
  },
        
  title: 'Review Creatives',
      
  mixins: [formInputMixin, copyToClipboardMixin, pageTrackingMixin],
        
  data() {
    return {
      loadingCreatives: false,
      filterByCreativeId: '',
      statusFilter: 'Pending',
      isLoadingSaveSettings: false,
      medicareAutoApproval: false,
      medicareApproveExisting: false,
      noLiabilityAccepted: false,
      medicareCategory: {},
    }
  },
  
  computed: {
    ...mapState('creative', [ 'creativeReviews','selectedCreativeReview' ]),
    ...mapGetters('creative', ['getSelectedCreativeReviewUrls', 'suggestedCreativesForReviewByBuyer']),
    ...mapGetters('common-entities', ['medicareCategories']),

    allCreativeReviewsFiltered() {
      return [...this.creativeReviews.filter(creative => !creative.isRecommendedForReview)]
    },

    suggestedCreativesForReviewCopy() {
      return [...this.suggestedCreativesForReviewByBuyer]
    }
  },

  watch: {    
    medicareCategory: {
      immediate: true,
      deep: false,
      handler(newValue) {   
        this.medicareAutoApproval = !!this.accountDetails?.creativeSettings?.categoryAutoApprovals?.includes(newValue.id)
      }
    }
  },
    
  async created() {
    try {   
      this.loadingCreatives = true  
      
      await this.getCategories()
      if (this.medicareCategories) {
        this.medicareCategory = this.medicareCategories[0]
      } 
      
      await this.getCreativeReviews()
      
      this.statusFilter = this.startCase(this.$route.query.status ?? 'Pending')
    } catch (error) {
      showErrorMessage(error.response?.data?.title)
    }
    finally {
      this.loadingCreatives = false
    }
  },
  
  methods: {
    ...mapActions('creative', [ 'getCreativeReviews','getCreativeReview', 'approveCreativeReview', 'rejectCreativeReview' ]),
    ...mapMutations('creative', ['SET_SELECTED_CREATIVE_REVIEW']),
    ...mapActions('account', [ 'updateBuyerCreativeSettings' ]),
    ...mapActions('common-entities', ['getCategories']),
  
    getStatusMap(status) {
      const statusToVariantMap = {
        Pending: {variant:'secondary', text:'Review Pending'},
        Rejected: {variant:'danger', text:'Rejected'},
        Approved: {variant:'success', text:'Approved'},
        Disabled: {variant:'warning', text:'Disabled'}        
      }
      return statusToVariantMap[status] ?? {variant:'dark', text: ''}
    },

    showConfirmApproveCreative() {      
      this.$bvModal.show('confirm-approve-modal')
      this.lightboxVisible = false
    },

    showConfirmRejectCreative() {      
      this.$bvModal.show('confirm-reject-modal')
      this.lightboxVisible = false
    },
  
    async loadCreativeReview(review){
      if (!review.creative.files || !review.creative.files.length) {
        this.SET_SELECTED_CREATIVE_REVIEW(review)
        this.$bvModal.show('review-modal')
        return
      }

      try {
        this.currentLightboxIndex = 0

        this.$set(this.loadingFiles, review.id, true)

        await this.getCreativeReview(review.id)

        this.lightboxVisible = true

        this.shouldReview = true
      } catch (error) {
        showErrorMessage("Failed to open files")
      } finally {
        this.$set(this.loadingFiles, review.id, false)
      }
    },

    async approveCreative(){
      try {
        this.isLoadingApproved = true

        await this.approveCreativeReview(this.selectedCreativeReview.id)

        this.$bvModal.hide('confirm-approve-modal')
        this.$bvModal.hide('review-modal')
      } catch (error) {
        showErrorMessage("Failed to approve creative")
      } finally {
        this.isLoadingApproved = false
      }
    },

    async rejectCreative(){
      try {
        this.isLoadingRejected = true

        await this.rejectCreativeReview({
          id: this.selectedCreativeReview.id, 
          reason: this.selectedCreativeReview.rejectionReason
        })

        this.$bvModal.hide('confirm-reject-modal')
        this.$bvModal.hide('review-modal')
      } catch (error) {
        showErrorMessage("Failed to reject creative")
      } finally {
        this.isLoadingRejected = false
      }
    },

    onImageIndexChange(oldIndex, newIndex) {
      this.currentLightboxIndex = newIndex
    },

    async updateCreativeSettings() {
      if (!this.medicareAutoApproval && !this.noLiabilityAccepted) {
        return
      }

      this.isLoadingSaveSettings = true      
      
      try {
        const request = !this.medicareAutoApproval ? {
          categoryAutoApprovals: {
            [this.medicareCategory.id]: !!this.medicareApproveExisting
          },
          noLiabilityAccepted: true
        } : {
          categoryAutoApprovals: {}
        }
        await this.updateBuyerCreativeSettings(request)

        this.medicareAutoApproval = !this.medicareAutoApproval
        showSuccessMessage('Successfully updated creative settings')
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.$bvModal.hide('confirm-save-creative-settings')
        this.isLoadingSaveSettings = false
        this.medicareApproveExisting = false
        this.noLiabilityAccepted = false
      }
    }
  }
}
</script>
  <style lang="scss" scoped>
  </style>